// physics-calc.js — Open-ended physics expression calculator
// All computation in Rust/WASM via pnp-worker.js. Pure UI here.

export function initPhysicsCalc() {
  const root = document.getElementById('phys-calc-root');
  if (!root) return;

  // ── Styles ──────────────────────────────────────────────────────────────────
  const style = document.createElement('style');
  style.textContent = `
    #phys-calc-root {
      font-family: 'Inter', system-ui, sans-serif;
      color: #e2e8f0;
      max-width: 900px;
    }
    .pc-section {
      background: #1e293b;
      border: 1px solid #334155;
      border-radius: 10px;
      padding: 1.25rem 1.5rem;
      margin-bottom: 1rem;
    }
    .pc-label {
      display: block;
      font-size: 0.8rem;
      font-weight: 600;
      color: #94a3b8;
      margin-bottom: 0.4rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }
    .pc-textarea {
      width: 100%;
      background: #0d1117;
      border: 1px solid #334155;
      border-radius: 6px;
      color: #e2e8f0;
      font-family: 'JetBrains Mono', 'Fira Code', 'Courier New', monospace;
      font-size: 1rem;
      padding: 0.75rem 1rem;
      resize: vertical;
      box-sizing: border-box;
      outline: none;
      transition: border-color 0.15s;
    }
    .pc-textarea:focus { border-color: #6366f1; }
    .pc-input {
      background: #0d1117;
      border: 1px solid #334155;
      border-radius: 6px;
      color: #e2e8f0;
      font-size: 0.9rem;
      padding: 0.5rem 0.75rem;
      outline: none;
      transition: border-color 0.15s;
    }
    .pc-input:focus { border-color: #6366f1; }
    .pc-btn {
      background: #6366f1;
      border: none;
      border-radius: 6px;
      color: #fff;
      cursor: pointer;
      font-size: 0.9rem;
      font-weight: 600;
      padding: 0.55rem 1.2rem;
      transition: background 0.15s, opacity 0.15s;
    }
    .pc-btn:hover { background: #4f46e5; }
    .pc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .pc-btn-sm {
      background: #1e293b;
      border: 1px solid #334155;
      border-radius: 5px;
      color: #94a3b8;
      cursor: pointer;
      font-size: 0.78rem;
      padding: 0.3rem 0.65rem;
      transition: border-color 0.15s, color 0.15s;
    }
    .pc-btn-sm:hover { border-color: #6366f1; color: #e2e8f0; }
    .pc-btn-danger {
      background: transparent;
      border: 1px solid #475569;
      border-radius: 5px;
      color: #64748b;
      cursor: pointer;
      font-size: 0.8rem;
      padding: 0.25rem 0.55rem;
      transition: border-color 0.15s, color 0.15s;
    }
    .pc-btn-danger:hover { border-color: #ef4444; color: #ef4444; }
    .pc-var-row {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      margin-bottom: 0.5rem;
    }
    .pc-var-name {
      width: 120px;
      flex-shrink: 0;
    }
    .pc-var-val { flex: 1; }
    .pc-result-value {
      font-family: 'JetBrains Mono', monospace;
      font-size: 1.8rem;
      font-weight: 700;
      color: #a5b4fc;
      word-break: break-all;
    }
    .pc-result-unit {
      font-size: 1rem;
      color: #94a3b8;
      margin-left: 0.5rem;
    }
    .pc-ieee {
      font-size: 0.82rem;
      color: #64748b;
      margin-top: 0.35rem;
      font-family: monospace;
    }
    .pc-metrics {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
      gap: 0.75rem;
      margin-top: 1rem;
    }
    .pc-metric {
      background: #0d1117;
      border: 1px solid #334155;
      border-radius: 8px;
      padding: 0.7rem 1rem;
    }
    .pc-metric-label {
      font-size: 0.72rem;
      color: #64748b;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-bottom: 0.3rem;
    }
    .pc-metric-value {
      font-size: 1.05rem;
      font-weight: 600;
      font-family: monospace;
    }
    .pc-trust-high   { color: #34d399; }
    .pc-trust-medium { color: #fbbf24; }
    .pc-trust-low    { color: #f87171; }
    .pc-narrative {
      margin-top: 1rem;
      font-size: 0.85rem;
      line-height: 1.6;
      color: #94a3b8;
      background: #0d1117;
      border-left: 3px solid #6366f1;
      padding: 0.75rem 1rem;
      border-radius: 0 6px 6px 0;
    }
    .pc-consts-table {
      width: 100%;
      border-collapse: collapse;
      font-size: 0.82rem;
      margin-top: 0.75rem;
    }
    .pc-consts-table th {
      text-align: left;
      color: #64748b;
      font-weight: 600;
      padding: 0.3rem 0.5rem;
      border-bottom: 1px solid #334155;
    }
    .pc-consts-table td {
      padding: 0.3rem 0.5rem;
      color: #94a3b8;
      font-family: monospace;
      border-bottom: 1px solid #1e293b;
    }
    .pc-accordion-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      cursor: pointer;
      user-select: none;
    }
    .pc-accordion-header h4 {
      margin: 0;
      font-size: 0.9rem;
      color: #e2e8f0;
    }
    .pc-accordion-arrow {
      color: #64748b;
      font-size: 0.75rem;
      transition: transform 0.2s;
    }
    .pc-accordion-arrow.open { transform: rotate(180deg); }
    .pc-accordion-body { display: none; margin-top: 0.75rem; }
    .pc-accordion-body.open { display: block; }
    .pc-const-ref-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 0.4rem;
    }
    .pc-const-ref-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.35rem 0.6rem;
      background: #0d1117;
      border: 1px solid #1e293b;
      border-radius: 5px;
      gap: 0.75rem;
    }
    .pc-const-ref-name {
      font-family: monospace;
      font-size: 0.82rem;
      color: #818cf8;
      cursor: pointer;
      font-weight: 600;
    }
    .pc-const-ref-name:hover { color: #a5b4fc; text-decoration: underline; }
    .pc-const-ref-val {
      font-family: monospace;
      font-size: 0.78rem;
      color: #64748b;
    }
    .pc-example-list {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      margin-top: 0.75rem;
    }
    .pc-example-item {
      background: #0d1117;
      border: 1px solid #334155;
      border-radius: 6px;
      padding: 0.6rem 1rem;
      cursor: pointer;
      transition: border-color 0.15s;
    }
    .pc-example-item:hover { border-color: #6366f1; }
    .pc-example-title {
      font-size: 0.82rem;
      font-weight: 600;
      color: #818cf8;
      margin-bottom: 0.2rem;
    }
    .pc-example-expr {
      font-family: monospace;
      font-size: 0.82rem;
      color: #94a3b8;
    }
    .pc-status {
      font-size: 0.82rem;
      color: #64748b;
      margin-left: 0.75rem;
    }
    .pc-error {
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid #ef4444;
      border-radius: 6px;
      color: #fca5a5;
      font-size: 0.85rem;
      padding: 0.75rem 1rem;
      margin-top: 0.75rem;
    }
  `;
  document.head.appendChild(style);

  // ── Constants reference data ─────────────────────────────────────────────────
  const CONST_REF = [
    { name: 'c',         value: '2.99792458e+8',    desc: 'Speed of light (m/s)' },
    { name: 'G',         value: '6.67430e-11',       desc: 'Gravitational constant' },
    { name: 'h',         value: '6.62607015e-34',    desc: 'Planck constant (J·s)' },
    { name: 'hbar',      value: '1.054571817e-34',   desc: 'Reduced Planck (J·s)' },
    { name: 'k_B',       value: '1.380649e-23',      desc: 'Boltzmann constant (J/K)' },
    { name: 'e',         value: '1.602176634e-19',   desc: 'Elementary charge (C)' },
    { name: 'm_e',       value: '9.1093837015e-31',  desc: 'Electron mass (kg)' },
    { name: 'm_p',       value: '1.67262192369e-27', desc: 'Proton mass (kg)' },
    { name: 'm_n',       value: '1.67492749804e-27', desc: 'Neutron mass (kg)' },
    { name: 'epsilon_0', value: '8.8541878128e-12',  desc: 'Vacuum permittivity (F/m)' },
    { name: 'mu_0',      value: '1.25663706212e-6',  desc: 'Vacuum permeability (H/m)' },
    { name: 'N_A',       value: '6.02214076e+23',    desc: 'Avogadro number (mol⁻¹)' },
    { name: 'R',         value: '8.314462618',        desc: 'Gas constant (J/mol/K)' },
    { name: 'sigma',     value: '5.670374419e-8',    desc: 'Stefan-Boltzmann (W/m²/K⁴)' },
    { name: 'alpha',     value: '7.2973525693e-3',   desc: 'Fine-structure constant' },
    { name: 'a_0',       value: '5.29177210903e-11', desc: 'Bohr radius (m)' },
    { name: 'pi',        value: '3.14159265358979',  desc: 'π' },
    { name: 'tau',       value: '6.28318530717959',  desc: '2π' },
    { name: 'inf',       value: '∞',                 desc: 'Infinity' },
  ];

  // ── Example expressions ────────────────────────────────────────────────────
  const EXAMPLES = [
    {
      title: 'Hawking Temperature (solar-mass BH)',
      expr: 'hbar * c^3 / (8 * pi * G * M * k_B)',
      vars: [{ name: 'M', value: '1.989e30' }],
      unit: 'K',
    },
    {
      title: 'Escape Velocity (Earth)',
      expr: 'sqrt(2 * G * M / R_planet)',
      vars: [{ name: 'M', value: '5.972e24' }, { name: 'R_planet', value: '6.371e6' }],
      unit: 'm/s',
    },
    {
      title: 'Photon Energy (500 nm visible light)',
      expr: 'h * c / lambda',
      vars: [{ name: 'lambda', value: '500e-9' }],
      unit: 'J',
    },
    {
      title: 'Fine-structure constant check (should ≈ 7.297e-3)',
      expr: 'e^2 / (4 * pi * epsilon_0 * hbar * c)',
      vars: [],
      unit: '',
    },
    {
      title: 'Bohr radius check (should ≈ 5.292e-11 m)',
      expr: 'hbar^2 / (m_e * e^2 / (4 * pi * epsilon_0))',
      vars: [],
      unit: 'm',
    },
  ];

  // ── HTML ───────────────────────────────────────────────────────────────────
  root.innerHTML = `
    <div style="margin-bottom:1.25rem;">
      <h3 style="margin:0 0 0.25rem; font-size:1.1rem; color:#e2e8f0;">Physics Expression Calculator</h3>
      <p style="margin:0; font-size:0.85rem; color:#64748b;">
        Enter any expression using built-in constants, operators, and functions.
        Evaluated in Rust/WASM with GeoNum precision tracking.
      </p>
    </div>

    <!-- Expression input -->
    <div class="pc-section">
      <label class="pc-label">Expression</label>
      <textarea id="pc-expr" class="pc-textarea" rows="3"
        placeholder="hbar * c^3 / (8 * pi * G * M * k_B)"></textarea>

      <div style="display:flex; gap:0.75rem; align-items:center; margin-top:0.75rem; flex-wrap:wrap;">
        <label class="pc-label" style="margin:0; white-space:nowrap;">Unit</label>
        <input id="pc-unit" class="pc-input" type="text" placeholder="K" style="width:80px;">
        <button id="pc-run" class="pc-btn">Run</button>
        <span id="pc-status" class="pc-status"></span>
      </div>
    </div>

    <!-- Variables -->
    <div class="pc-section">
      <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:0.75rem;">
        <label class="pc-label" style="margin:0;">Variables</label>
        <button id="pc-add-var" class="pc-btn-sm">+ Add Variable</button>
      </div>
      <div id="pc-vars"></div>
      <p id="pc-vars-empty" style="margin:0; font-size:0.82rem; color:#475569; font-style:italic;">
        No variables defined. Built-in constants (c, G, hbar, …) are always available.
      </p>
    </div>

    <!-- Result -->
    <div class="pc-section" id="pc-result-section" style="display:none;">
      <label class="pc-label">Result</label>
      <div>
        <span class="pc-result-value" id="pc-result-val">—</span>
        <span class="pc-result-unit" id="pc-result-unit"></span>
      </div>
      <div class="pc-ieee" id="pc-ieee-val"></div>

      <div class="pc-metrics" id="pc-metrics"></div>

      <div id="pc-consts-used-section" style="display:none; margin-top:1rem;">
        <label class="pc-label">Constants Used</label>
        <table class="pc-consts-table">
          <thead><tr><th>Name</th><th>Value</th></tr></thead>
          <tbody id="pc-consts-used-body"></tbody>
        </table>
      </div>

      <div class="pc-narrative" id="pc-narrative"></div>
    </div>

    <div class="pc-error" id="pc-error-box" style="display:none;"></div>

    <!-- Constants reference -->
    <div class="pc-section">
      <div class="pc-accordion-header" id="pc-const-ref-toggle">
        <h4>Built-in Constants Reference</h4>
        <span class="pc-accordion-arrow" id="pc-const-ref-arrow">▼</span>
      </div>
      <div class="pc-accordion-body" id="pc-const-ref-body">
        <p style="font-size:0.78rem; color:#64748b; margin:0 0 0.75rem;">
          Click a constant name to insert it at the cursor position in the expression field.
        </p>
        <div class="pc-const-ref-grid" id="pc-const-ref-grid"></div>
      </div>
    </div>

    <!-- Examples -->
    <div class="pc-section">
      <div class="pc-accordion-header" id="pc-examples-toggle">
        <h4>Example Expressions</h4>
        <span class="pc-accordion-arrow" id="pc-examples-arrow">▼</span>
      </div>
      <div class="pc-accordion-body" id="pc-examples-body">
        <div class="pc-example-list" id="pc-example-list"></div>
      </div>
    </div>
  `;

  // ── Element refs ─────────────────────────────────────────────────────────────
  const exprEl   = root.querySelector('#pc-expr');
  const unitEl   = root.querySelector('#pc-unit');
  const runBtn   = root.querySelector('#pc-run');
  const statusEl = root.querySelector('#pc-status');
  const varsEl   = root.querySelector('#pc-vars');
  const varsEmpty = root.querySelector('#pc-vars-empty');
  const addVarBtn = root.querySelector('#pc-add-var');
  const resultSection = root.querySelector('#pc-result-section');
  const resultValEl   = root.querySelector('#pc-result-val');
  const resultUnitEl  = root.querySelector('#pc-result-unit');
  const ieeeValEl     = root.querySelector('#pc-ieee-val');
  const metricsEl     = root.querySelector('#pc-metrics');
  const constsUsedSec = root.querySelector('#pc-consts-used-section');
  const constsUsedBody = root.querySelector('#pc-consts-used-body');
  const narrativeEl   = root.querySelector('#pc-narrative');
  const errorBox      = root.querySelector('#pc-error-box');

  // ── Variables management ──────────────────────────────────────────────────
  let varRows = [];

  function refreshVarsEmpty() {
    varsEmpty.style.display = varRows.length === 0 ? '' : 'none';
  }

  function addVarRow(name = '', value = '') {
    const row = document.createElement('div');
    row.className = 'pc-var-row';

    const nameInput = document.createElement('input');
    nameInput.className = 'pc-input pc-var-name';
    nameInput.type = 'text';
    nameInput.placeholder = 'name';
    nameInput.value = name;

    const valInput = document.createElement('input');
    valInput.className = 'pc-input pc-var-val';
    valInput.type = 'text';
    valInput.placeholder = '1.23e4';
    valInput.value = value;

    const removeBtn = document.createElement('button');
    removeBtn.className = 'pc-btn-danger';
    removeBtn.textContent = '×';
    removeBtn.title = 'Remove variable';
    removeBtn.addEventListener('click', () => {
      row.remove();
      varRows = varRows.filter(r => r !== row);
      refreshVarsEmpty();
    });

    row.appendChild(nameInput);
    row.appendChild(valInput);
    row.appendChild(removeBtn);
    row._nameInput = nameInput;
    row._valInput  = valInput;

    varsEl.appendChild(row);
    varRows.push(row);
    refreshVarsEmpty();
  }

  addVarBtn.addEventListener('click', () => addVarRow());

  function collectVariables() {
    const vars = {};
    for (const row of varRows) {
      const name = row._nameInput.value.trim();
      const val  = parseFloat(row._valInput.value);
      if (name && !isNaN(val)) vars[name] = val;
    }
    return vars;
  }

  // ── Constants reference panel ─────────────────────────────────────────────
  const constRefGrid = root.querySelector('#pc-const-ref-grid');
  for (const c of CONST_REF) {
    const item = document.createElement('div');
    item.className = 'pc-const-ref-item';

    const nameSpan = document.createElement('span');
    nameSpan.className = 'pc-const-ref-name';
    nameSpan.textContent = c.name;
    nameSpan.title = c.desc;
    nameSpan.addEventListener('click', () => insertAtCursor(exprEl, c.name));

    const valSpan = document.createElement('span');
    valSpan.className = 'pc-const-ref-val';
    valSpan.textContent = c.value;

    item.appendChild(nameSpan);
    item.appendChild(valSpan);
    constRefGrid.appendChild(item);
  }

  // ── Examples panel ────────────────────────────────────────────────────────
  const exampleList = root.querySelector('#pc-example-list');
  for (const ex of EXAMPLES) {
    const item = document.createElement('div');
    item.className = 'pc-example-item';

    const title = document.createElement('div');
    title.className = 'pc-example-title';
    title.textContent = ex.title;

    const expr = document.createElement('div');
    expr.className = 'pc-example-expr';
    expr.textContent = ex.expr;

    item.appendChild(title);
    item.appendChild(expr);

    item.addEventListener('click', () => {
      exprEl.value = ex.expr;
      unitEl.value = ex.unit;
      varRows.forEach(r => r.remove());
      varRows = [];
      for (const v of ex.vars) addVarRow(v.name, v.value);
      refreshVarsEmpty();
    });

    exampleList.appendChild(item);
  }

  // ── Accordion toggles ─────────────────────────────────────────────────────
  function setupAccordion(toggleId, bodyId, arrowId) {
    const toggle = root.querySelector(`#${toggleId}`);
    const body   = root.querySelector(`#${bodyId}`);
    const arrow  = root.querySelector(`#${arrowId}`);
    toggle.addEventListener('click', () => {
      const open = body.classList.toggle('open');
      arrow.classList.toggle('open', open);
    });
  }

  setupAccordion('pc-const-ref-toggle', 'pc-const-ref-body', 'pc-const-ref-arrow');
  setupAccordion('pc-examples-toggle',  'pc-examples-body',  'pc-examples-arrow');

  // ── Worker setup ───────────────────────────────────────────────────────────
  let worker = null;
  let pendingResolvers = {};
  let msgIdCounter = 0;

  function getWorker() {
    if (!worker) {
      worker = new Worker('./js/pnp-worker.js', { type: 'module' });
      window.gdbslog?.instrumentWorker(worker, 'physics');
      worker.onmessage = (e) => {
        const { id, type, result, message } = e.data;
        if (type === 'progress') {
          statusEl.textContent = message;
          return;
        }
        const handlers = pendingResolvers[id];
        if (!handlers) return;
        delete pendingResolvers[id];
        if (type === 'result') handlers.resolve(result);
        else handlers.reject(new Error(message || 'Unknown worker error'));
      };
      worker.onerror = (e) => {
        Object.values(pendingResolvers).forEach(h => h.reject(e));
        pendingResolvers = {};
        worker = null;
      };
    }
    return worker;
  }

  function callWorker(type, payload) {
    return new Promise((resolve, reject) => {
      const id = ++msgIdCounter;
      pendingResolvers[id] = { resolve, reject };
      getWorker().postMessage({ id, type, payload });
    });
  }

  // ── Formatting helpers ─────────────────────────────────────────────────────
  function fmtNum(v) {
    if (v === null || v === undefined) return '—';
    if (!isFinite(v)) return v > 0 ? '+∞' : (v < 0 ? '-∞' : 'NaN');
    const abs = Math.abs(v);
    if (abs === 0) return '0';
    if (abs >= 1e-3 && abs < 1e6) return parseFloat(v.toPrecision(8)).toString();
    return v.toExponential(6);
  }

  // ── Run ────────────────────────────────────────────────────────────────────
  async function runCalc() {
    const expression = exprEl.value.trim();
    if (!expression) { statusEl.textContent = 'Enter an expression.'; return; }

    errorBox.style.display = 'none';
    resultSection.style.display = 'none';
    runBtn.disabled = true;
    statusEl.textContent = 'Running…';

    try {
      const payload = {
        expression,
        variables: collectVariables(),
        unit: unitEl.value.trim(),
      };

      // The run is audited by instrumentWorker(worker, 'physics') (op
      // 'physics_calc (worker)'); no explicit gdbslog.time wrap here or it would
      // log the same run twice in the activity view.
      const res = await callWorker('physics_calc', payload);

      if (res.error) {
        showError(res.error);
        statusEl.textContent = 'Error';
        return;
      }

      displayResult(res, expression);
      statusEl.textContent = 'Done';
    } catch (err) {
      showError(err.message);
      statusEl.textContent = 'Error';
    } finally {
      runBtn.disabled = false;
    }
  }

  runBtn.addEventListener('click', runCalc);
  exprEl.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) runCalc();
  });

  // ── Display result ─────────────────────────────────────────────────────────
  function displayResult(res, expression) {
    const gv = res.result_geonum;
    resultValEl.textContent = fmtNum(gv);
    resultUnitEl.textContent = res.unit || '';
    ieeeValEl.textContent = `IEEE 754: ${fmtNum(res.result)}`;

    const drift = res.drift_shades;
    const relUnc = res.relative_uncertainty;
    const trust = res.trust_level;
    const trustClass = trust === 'HIGH' ? 'pc-trust-high' : trust === 'MEDIUM' ? 'pc-trust-medium' : 'pc-trust-low';

    metricsEl.innerHTML = `
      <div class="pc-metric">
        <div class="pc-metric-label">GeoNum Drift</div>
        <div class="pc-metric-value ${trustClass}">${drift !== null ? drift.toFixed(4) : '—'} shades</div>
      </div>
      <div class="pc-metric">
        <div class="pc-metric-label">Relative Uncertainty</div>
        <div class="pc-metric-value">${relUnc !== null ? relUnc.toExponential(3) : '—'}</div>
      </div>
      <div class="pc-metric">
        <div class="pc-metric-label">Trust Level</div>
        <div class="pc-metric-value ${trustClass}">${trust || '—'}</div>
      </div>
    `;

    if (res.constants_used && res.constants_used.length > 0) {
      constsUsedBody.innerHTML = res.constants_used
        .map(([n, v]) => `<tr><td>${n}</td><td>${v}</td></tr>`)
        .join('');
      constsUsedSec.style.display = '';
    } else {
      constsUsedSec.style.display = 'none';
    }

    narrativeEl.textContent = res.narrative || '';

    resultSection.style.display = '';
    errorBox.style.display = 'none';
  }

  function showError(msg) {
    errorBox.textContent = `Error: ${msg}`;
    errorBox.style.display = '';
    resultSection.style.display = 'none';
  }

  // ── Insert at cursor ───────────────────────────────────────────────────────
  function insertAtCursor(el, text) {
    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const before = el.value.substring(0, start);
    const after  = el.value.substring(end);
    el.value = before + text + after;
    const pos = start + text.length;
    el.setSelectionRange(pos, pos);
    el.focus();
  }
}
